import { useNavigate, useParams } from "react-router-dom";
import { ApolloError } from "@apollo/client";
import { Layout } from "../UI/Layout/Layout.tsx";
import { ScreenTitleBlock } from "../UI/Layout/ScreenTitleBlock.tsx";
import { BreadCrumbsWrapper } from "../UI/Layout/BreadCrumbsWrapper.tsx";
import { BreadCrumbsItem } from "../UI/Layout/BreadCrumbsItem.tsx";
import { Card } from "../UI/Card.tsx";
import { LoadingRows } from "../UI/Loading/LoadingRows.tsx";
import { ErrorMessagePopup } from "../UI/MessagePopup/ErrorMessagePopup.tsx";
import { useMessagePopupController } from "../UI/MessagePopup/useMessagePopupController.ts";
import { UserGroupForm, UserGroupFormInputs } from "./UserGroupForm.tsx";
import { useAllUserGroups } from "./useAllUserGroups.ts";
import { useToAllUserGroups } from "./useToAllUserGroups.ts";
import { useUpdateUserGroup } from "./useUpdateUserGroup.ts";
import { WeekDayEnum } from "./WeekDayEnum.tsx";

export const EditUserGroupScreenPath = "/user-groups/:userGroupId/edit";

export function EditUserGroupScreen() {
  const { userGroupId } = useParams<{ userGroupId: string }>();
  const { userGroups, loading } = useAllUserGroups();
  const { toAllUserGroups } = useToAllUserGroups();
  const { updateUserGroup } = useUpdateUserGroup();
  const navigate = useNavigate();
  const { show, title, description, showPopup } = useMessagePopupController(
    4 * 1000,
  );
  const userGroup = userGroups.find((g) => g.id === userGroupId);

  async function onSave(data: UserGroupFormInputs) {
    if (!userGroup) return;
    try {
      await updateUserGroup({
        id: userGroup.id,
        availability: data.availability ?? [],
        onboardingLiveRequired: data.onboardingLiveRequired,
        onboardingLiveBookingWindowDays: data.onboardingLiveBookingWindowDays,
      });
    } catch (e) {
      const error = e as ApolloError;
      showPopup("Error", error?.message ?? "Unknown error");
      return;
    }
    navigate(-1);
  }

  return (
    <>
      <ErrorMessagePopup title={title} description={description} show={show} />
      <Layout>
        <Card>
          <ScreenTitleBlock
            title={userGroup ? `Edit ${userGroup.name}` : "Edit user group"}
            breadCrumbs={
              <BreadCrumbsWrapper>
                <BreadCrumbsItem
                  onClick={toAllUserGroups}
                  title={"User Groups"}
                />
                <BreadCrumbsItem title={userGroup?.name ?? "Edit"} />
              </BreadCrumbsWrapper>
            }
          />
        </Card>
        <div className={"mt-4"}>
          <Card>
            {loading && !userGroup ? (
              <LoadingRows />
            ) : userGroup ? (
              <UserGroupForm
                isEditing
                onSave={onSave}
                defaultValues={{
                  name: userGroup.name,
                  thinkificBundleId: userGroup.thinkificBundleId ?? undefined,
                  onboardingLiveRequired: userGroup.onboardingLiveRequired,
                  onboardingLiveBookingWindowDays:
                    userGroup.onboardingLiveBookingWindowDays,
                  availability: (userGroup.availability ?? []).map((a) => ({
                    weekDay: a.weekDay as unknown as WeekDayEnum,
                    timeIntervals: a.timeIntervals.map((ti) => ({
                      from: ti.from,
                      to: ti.to,
                    })),
                  })),
                }}
              />
            ) : (
              <div className={"text-sm text-slate-500"}>
                User group not found
              </div>
            )}
          </Card>
        </div>
      </Layout>
    </>
  );
}
